import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AppLoader } from '@hero/atoms';
import { AvailableCharitiesModal, DonateToCharityModal } from '@hero/components/modals';
import { RECOGNITION_CREATION_STEPS } from '@hero/constants';
import { useGetCharity, useRecognitionCreationHeader } from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { ProductSelectionSection, WidgetFooter } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useUpdateRecognition } from '@hero/services';
import { useNewRecognitionStore } from '@hero/store/hooks';
import { useValidateRules } from '@hero/store/hooks/useValidateRules';
import { getProductsRule } from '@hero/validations';

import * as S from './style';

function NewRecognitionGifts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { charityEnabled, isCharityExist, anyCharityItemExists } = useGetCharity();

  const [isPageLoading, setIsPageLoading] = React.useState(false);
  const [showCharityInfoModal, setShowCharityInfoModal] = React.useState(false);
  const [showSelectCharitiesModal, setShowSelectCharitiesModal] = React.useState(false);

  const header = useRecognitionCreationHeader({
    step: 1,
    setIsPageLoading
  });
  const { code, products, updateProducts } = useNewRecognitionStore();
  const { mutateAsync: updateRecognition, isLoading: isLoadingUpdateRecognition } =
    useUpdateRecognition();

  const selectedSkus = useMemo(() => {
    return products.reduce((r, p) => r.concat(p.skus), []);
  }, [products]);

  const backButtonOnClick = () => navigate(ROUTE_PATHS.MY_RECOGNITIONS);

  const { validate } = useValidateRules();

  const onNextButtonClick = async ({ skipCharity }) => {
    const isValid = await validate([{ rule: getProductsRule(), value: products }]);
    if (!isValid) return;

    const hasCharity = anyCharityItemExists(products);
    if (!hasCharity && charityEnabled && !skipCharity && isCharityExist)
      return setShowCharityInfoModal(true);

    setIsPageLoading(true);

    try {
      await updateRecognition({
        params: {
          recognitionCode: code
        },
        body: {
          products,
          meta: {
            creationStep: RECOGNITION_CREATION_STEPS.newRecognitionContactType
          }
        }
      });

      navigate(ROUTE_PATHS.NEW_RECOGNITION_CONTACT_TYPE);
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    if (!code) {
      navigate(ROUTE_PATHS.MY_RECOGNITIONS);
    }
  }, []);

  return (
    <PageLayout
      header={header}
      navigation={{
        leftButton: {
          children: t('back'),
          onClick: backButtonOnClick
        },
        rightButton: {
          icon: true,
          children: t('next'),
          onClick: onNextButtonClick,
          loading: isLoadingUpdateRecognition
        }
      }}
    >
      <S.HeadTitle variant="subtitle4Bold">{t('giftListHeaderSubTitle')}</S.HeadTitle>
      <ProductSelectionSection
        featureType="recognition"
        selectedProducts={products}
        onChangeSelectedProductList={selectedProducts => {
          updateProducts(selectedProducts);
        }}
        localShoppingCartEnabled
        components={{
          WidgetFooter: () => (
            <WidgetFooter
              products={products}
              selectedSkus={selectedSkus}
              onNextButtonClick={onNextButtonClick}
              isLoading={isPageLoading}
            />
          )
        }}
        isDraft
      />
      {isPageLoading && <AppLoader />}
      {showCharityInfoModal && (
        <DonateToCharityModal
          isOpen={showCharityInfoModal}
          setIsOpen={setShowCharityInfoModal}
          primaryButtonProps={{
            onClick: () => {
              setShowCharityInfoModal(false);
              setShowSelectCharitiesModal(true);
            },
            children: t('yes')
          }}
          secondaryButtonProps={{
            onClick: () => {
              setShowCharityInfoModal(false);
              onNextButtonClick({ skipCharity: true });
            },
            children: t('skip')
          }}
          descriptionTextKey="donateToCharityDesc2"
          titleKey="donateToCharityTitle2"
        />
      )}
      {showSelectCharitiesModal && (
        <AvailableCharitiesModal
          isOpen={showSelectCharitiesModal}
          setIsOpen={setShowSelectCharitiesModal}
          setSelectedCharities={selectedCharities =>
            updateProducts([...products, ...selectedCharities])
          }
        />
      )}
    </PageLayout>
  );
}

export { NewRecognitionGifts };
